import React, { useEffect, useState } from "react";
import { liff } from "@line/liff";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";

const LiffLogin = () => {
  const navigate = useNavigate();
  const { validateAndSetUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [isApproved, setIsApproved] = useState(false);

  useEffect(() => {
    const initializeLiff = async () => {
      try {
        await liff.init({ liffId: import.meta.env.VITE_LIFF_ID });
        if (!liff.isLoggedIn()) {
          liff.login();
        } else {
          const userProfile = await liff.getProfile();
          setProfile(userProfile);
        }
      } catch (err) {
        setError("กรุณาติดต่อเจ้าหน้าที่ ให้อนุญาติ");
      }
    };

    initializeLiff();

    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleLogin = async () => {
    if (!profile) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/auth/line/login`,
        {
          userId: profile.userId,
          displayName: profile.displayName,
          pictureUrl: profile.pictureUrl,
        }
      );
      
      // Successful login
      await validateAndSetUser(response.data.access_token);
      setIsApproved(true);
      
      // Wait 2 seconds then redirect
      setTimeout(() => {
        navigate("/line/dashboard");
      }, 2000);
      
    } catch (err) {
      setLoading(false);
      
      // Check if it's a 4xx error (user not approved)
      if (err.response && err.response.status >= 400 && err.response.status < 500) {
        setError("กรุณาติดต่อเจ้าหน้าที่ ให้อนุญาติ");
      } else {
        setError("กรุณาติดต่อเจ้าหน้าที่ ให้อนุญาติ");
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative">
      {/* Loading - centered and smaller, only shown when loading and no profile */}
      {loading && !profile && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-64 h-64 relative">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover rounded-lg"
            >
              <source src="/vdo/loading.mp4" type="video/mp4" />
              <img src="/images/main.png" alt="loading" className="w-full h-full object-cover rounded-lg" />
            </video>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          <p>{error}</p>
        </div>
      )}

      {/* Profile section */}
      {profile && (
        <div className="text-center">
          {/* Status Image */}
          <img
            src={
              isApproved
                ? "/images/success.png"
                : error
                ? "/images/sad.png"
                : "/images/main.png"
            }
            alt="Status"
            className="w-20 h-auto mx-auto mb-4"
          />
          <img
            src={profile.pictureUrl}
            alt={profile.displayName}
            className="w-24 h-24 rounded-full mx-auto"
          />
          <h1 className="text-2xl font-bold mt-4">{profile.displayName}</h1>
          <p className="text-gray-600">{profile.statusMessage}</p>
          
          {isApproved ? (
            <div className="mt-8">
              <p className="text-green-600 mb-4">เข้าสู่ระบบสำเร็จ กำลังเปลี่ยนหน้า...</p>
              {loading && (
                <div className="flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={handleLogin}
              disabled={loading}
              className={`mt-8 ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-500 hover:bg-blue-700'
              } text-white font-bold py-2 px-4 rounded flex items-center justify-center mx-auto`}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  กำลังเชื่อมต่อกับ Line...
                </>
              ) : (
                'เข้าสู่ระบบ'
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LiffLogin;
